<template>
  <div class="relative">
    <!-- Bell Icon Button -->
    <button 
      @click="toggleDropdown"
      class="rounded-full w-6 h-6 text-white button-interactive relative focus:outline-none focus:ring-2 focus:ring-primary-500"
      :class="{ 'text-primary-400': hasUnreadNotifications }"
      aria-label="Notificaciones"
    >
      <bell-icon class="w-full stroke-current" />
      
      <!-- Unread Badge -->
      <span 
        v-if="unreadCount > 0"
        class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium"
        :class="{ 'animate-pulse': hasNewNotifications }"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <!-- Notifications Dropdown -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isDropdownOpen"
        class="absolute right-0 mt-2 w-80 max-sm:fixed max-sm:top-16 max-sm:left-4 max-sm:right-4 max-sm:w-auto bg-content-800 border border-content-600 rounded-lg shadow-lg z-[9999]"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-content-600">
          <h3 class="text-lg font-medium text-white">Notificaciones</h3>
          <div class="flex items-center space-x-2">
            <button
              v-if="unreadCount > 0"
              @click="markAllAsRead"
              class="text-xs text-primary-400 hover:text-primary-300 button-interactive max-sm:text-xs max-sm:px-2"
            >
              <span class="max-sm:hidden">Marcar todas como leídas</span>
              <span class="sm:hidden">Marcar todas</span>
            </button>
            <button
              @click="closeDropdown"
              class="text-content-300 hover:text-white button-interactive"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Notifications List -->
        <div class="max-h-96 overflow-y-auto">
          <!-- Loading State -->
          <div v-if="loading" class="flex justify-center items-center py-8">
            <loader-icon class="h-6 w-6 text-primary-500 animate-spin"></loader-icon>
          </div>

          <!-- Empty State -->
          <div v-else-if="notifications.length === 0" class="p-6 text-center">
            <div class="text-content-300 mb-2">
              <bell-icon class="w-12 h-12 mx-auto stroke-current opacity-50" />
            </div>
            <p class="text-content-300 text-sm">No hay notificaciones</p>
          </div>

          <!-- Notifications -->
          <div v-else>
            <div
              v-for="notification in notifications"
              :key="notification.id"
              class="border-b border-content-700 last:border-b-0"
            >
              <div
                class="p-4 hover:bg-content-700 cursor-pointer transition-colors duration-150"
                :class="{ 'bg-content-750': !notification.isRead }"
                @click="handleNotificationClick(notification)"
              >
                <div class="flex items-start space-x-3">
                  <!-- Unread Indicator -->
                  <div
                    class="flex-shrink-0 w-2 h-2 rounded-full mt-2"
                    :class="notification.isRead ? 'bg-transparent' : 'bg-primary-500'"
                  ></div>
                  
                  <!-- Content -->
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-white mb-1">{{ notification.title }}</p>
                    <p class="text-sm text-content-200 line-clamp-2">{{ notification.message }}</p>
                    <p class="text-xs text-content-400 mt-2">{{ formatDate(notification.createdAt) }}</p>
                  </div>

                  <!-- Actions -->
                  <div class="flex-shrink-0 flex items-center space-x-2">
                    <button
                      v-if="notification.isRead"
                      @click.stop="markAsUnread(notification.id)"
                      class="text-xs text-content-400 hover:text-primary-300 button-interactive p-1"
                      title="Marcar como no leída"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      v-else
                      @click.stop="markAsRead(notification.id)"
                      class="text-xs text-content-400 hover:text-primary-300 button-interactive p-1"
                      title="Marcar como leída"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                    
                    <!-- Type Icon -->
                    <component 
                      :is="getNotificationIcon(notification)"
                      class="w-5 h-5 text-content-400"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="border-t border-content-600 p-3">
          <button
            @click="viewAllNotifications"
            class="w-full text-center text-sm text-primary-400 hover:text-primary-300 button-interactive py-2"
          >
            Ver todas las notificaciones
          </button>
        </div>
      </div>
    </transition>

    <!-- Click Outside Overlay -->
    <div
      v-if="isDropdownOpen"
      class="fixed inset-0 z-[9998]"
      @click="closeDropdown"
    ></div>
  </div>
</template>

<script>
import BellIcon from "../../icons/BellIcon";
import LoaderIcon from "../../icons/LoaderIcon";
import OpportunitiesIcon from "../../icons/OpportunitiesIcon";
import IntentionsIcon from "../../icons/IntentionsIcon";
import api from "../../api";

export default {
  name: "NotificationBell",
  components: {
    BellIcon,
    LoaderIcon,
    OpportunitiesIcon,
    IntentionsIcon
  },
  data() {
    return {
      isDropdownOpen: false,
      loading: false,
      notifications: [],
      unreadCount: 0,
      hasNewNotifications: false,
      pollInterval: null,
      isTabActive: true,
      pollFrequency: 5000, // 5 seconds when active
      inactivePollFrequency: 30000 // 30 seconds when inactive
    };
  },
  computed: {
    hasUnreadNotifications() {
      return this.unreadCount > 0;
    }
  },
  async created() {
    await this.loadUnreadCount();
    await this.loadNotifications();
    this.setupVisibilityHandlers();
    this.requestNotificationPermission();
    this.startPolling();
  },
  beforeUnmount() {
    this.stopPolling();
    this.removeVisibilityHandlers();
  },
  methods: {
    async toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
      if (this.isDropdownOpen) {
        await this.loadNotifications();
      }
    },

    closeDropdown() {
      this.isDropdownOpen = false;
    },

    async loadNotifications() {
      if (this.loading) return;
      
      this.loading = true;
      try {
        const response = await api.notifications.getNotifications({ limit: 10 });
        this.notifications = response.notifications || [];
      } catch (error) {
        console.error('Error loading notifications:', error);
        // Don't show error message on initial load, just set empty array
        this.notifications = [];
      } finally {
        this.loading = false;
      }
    },

    async loadUnreadCount() {
      try {
        const response = await api.notifications.getUnreadCount();
        const newUnreadCount = response.unreadCount || response.count || 0;
        
        // Check if there are new notifications
        if (newUnreadCount > this.unreadCount) {
          this.hasNewNotifications = true;
          
          // Show browser notification if user allowed it and tab is not active
          if (!this.isTabActive && 'Notification' in window && Notification.permission === 'granted') {
            new Notification('Nueva notificación', {
              body: `Tienes ${newUnreadCount} notificación${newUnreadCount > 1 ? 'es' : ''} sin leer`,
              icon: '/favicon.ico',
              badge: '/favicon.ico'
            });
          }
          
          // Clear the animation after 3 seconds
          setTimeout(() => {
            this.hasNewNotifications = false;
          }, 3000);
        }
        
        this.unreadCount = newUnreadCount;
      } catch (error) {
        console.error('Error loading unread count:', error);
        // Only reset count if it's a network error, not a server error
        if (!error.response) {
          this.unreadCount = 0;
        }
      }
    },

    async markAllAsRead() {
      try {
        await api.notifications.markAllAsRead();

        // Update local state
        this.notifications = this.notifications.map(n => ({ ...n, isRead: true }));
        this.unreadCount = 0;

        this.$store.dispatch("successSnack", "Todas las notificaciones marcadas como leídas");
      } catch (error) {
        console.error('Error marking all as read:', error);
        this.$store.dispatch("errorSnack", "Error al marcar notificaciones");
      }
    },

    async handleNotificationClick(notification) {
      // Mark as read if unread
      if (!notification.isRead) {
        await this.markAsRead(notification.id);
      }

      // Handle navigation based on notification type
      this.navigateToNotificationTarget(notification);
      this.closeDropdown();
    },

    async markAsRead(notificationId) {
      // Prevent multiple calls for the same notification
      const notification = this.notifications.find(n => n.id === notificationId);
      if (!notification || notification.isRead) {
        return;
      }

      try {
        await api.notifications.markAsRead(notificationId);

        // Update local state
        notification.isRead = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },

    async markAsUnread(notificationId) {
      try {
        await api.notifications.markAsUnread(notificationId);

        // Update local state
        const notification = this.notifications.find(n => n.id === notificationId);
        if (notification) {
          notification.isRead = false;
          this.unreadCount = this.unreadCount + 1;
        }
      } catch (error) {
        console.error('Error marking notification as unread:', error);
      }
    },

    navigateToNotificationTarget(notification) {
      // Use Vue Router navigation instead of direct URLs for proper routing
      const data = notification.data;
      
      if (!data) return;

      try {
        switch (data.type) {
          case 'opportunity':
            if (this.$store.getters.userType === 'admin') {
              this.$router.push({ 
                name: 'adminOpportunityDetail', 
                params: { id: data.opportunityId } 
              });
            } else {
              this.$router.push({ 
                name: 'opportunityDetail', 
                params: { id: data.opportunityId } 
              });
            }
            break;
            
          case 'intention':
            if (this.$store.getters.userType === 'admin') {
              // Redirigir a la lista de intenciones con filtros para destacar la intención específica
              this.$router.push({ 
                name: 'adminIntentionsIndex',
                query: { 
                  clientId: data.clientId,
                  opportunityId: data.opportunityId,
                  highlight: data.intentionId
                }
              });
            }
            break;
            
          default:
            console.log('Unknown notification type:', data.type);
        }
      } catch (error) {
        console.error('Error navigating to notification target:', error);
      }
    },

    viewAllNotifications() {
      this.closeDropdown();
      
      if (this.$store.getters.userType === 'admin') {
        this.$router.push({ name: 'adminNotifications' });
      } else {
        this.$router.push({ name: 'clientNotifications' });
      }
    },

    getNotificationIcon(notification) {
      const data = notification.data;
      if (!data) return 'BellIcon';
      
      switch (data.type) {
        case 'opportunity':
          return 'OpportunitiesIcon';
        case 'intention':
          return 'IntentionsIcon';
        default:
          return 'BellIcon';
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 1) {
        return 'Hace unos minutos';
      } else if (diffInHours < 24) {
        return `Hace ${Math.floor(diffInHours)} horas`;
      } else if (diffInHours < 48) {
        return 'Ayer';
      } else {
        return date.toLocaleDateString('es-MX', {
          month: 'short',
          day: 'numeric'
        });
      }
    },

    startPolling() {
      // Clear existing interval
      this.stopPolling();
      
      // Use dynamic frequency based on tab visibility
      const frequency = this.isTabActive ? this.pollFrequency : this.inactivePollFrequency;
      
      this.pollInterval = setInterval(() => {
        this.loadUnreadCount();
        // Also refresh notifications if dropdown is open
        if (this.isDropdownOpen) {
          this.loadNotifications();
        }
      }, frequency);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    setupVisibilityHandlers() {
      // Handle tab visibility changes
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      // Handle window focus/blur
      window.addEventListener('focus', this.handleWindowFocus);
      window.addEventListener('blur', this.handleWindowBlur);
    },

    removeVisibilityHandlers() {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('focus', this.handleWindowFocus);
      window.removeEventListener('blur', this.handleWindowBlur);
    },

    handleVisibilityChange() {
      const wasActive = this.isTabActive;
      this.isTabActive = !document.hidden;
      
      // Restart polling with new frequency if visibility changed
      if (wasActive !== this.isTabActive) {
        this.startPolling();
        
        // Load immediately when tab becomes active
        if (this.isTabActive) {
          this.loadUnreadCount();
          if (this.isDropdownOpen) {
            this.loadNotifications();
          }
        }
      }
    },

    handleWindowFocus() {
      if (!this.isTabActive) {
        this.isTabActive = true;
        this.startPolling();
        this.loadUnreadCount();
        if (this.isDropdownOpen) {
          this.loadNotifications();
        }
      }
    },

    handleWindowBlur() {
      if (this.isTabActive) {
        this.isTabActive = false;
        this.startPolling();
      }
    },

    async requestNotificationPermission() {
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        return;
      }

      // Request permission if not already granted or denied
      if (Notification.permission === 'default') {
        try {
          await Notification.requestPermission();
        } catch (error) {
          console.log('Notification permission request failed:', error);
        }
      }
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>